import type { TimedModeId } from "./gameplay-config";
import type { SeasonalChallengeIdentity } from "./challenge-link-config";

export type SeasonalChallengeSlug = string;
export type SeasonalChallengeStatus =
  "active" | "disabled" | "expired" | "unknown" | "upcoming";

export interface SeasonalArenaPalette {
  readonly backgroundColor: number;
  readonly boundaryColor: number;
  readonly boundaryOutlineColor: number;
  readonly decorationColor: number;
  readonly fieldColor: number;
  readonly foodHaloColor: number;
  readonly snakeOutlineColor: number;
}

export interface SeasonalArenaDecoration {
  readonly alpha: number;
  readonly color?: number;
  readonly heightRatio: number;
  readonly kind:
    "bat" | "crescent" | "ellipse" | "mist" | "pumpkin" | "rectangle";
  readonly reducedMotion: "hidden" | "static";
  readonly widthRatio: number;
  readonly xRatio: number;
  readonly yRatio: number;
}

export interface SeasonalChallengeDescriptor {
  readonly accessibility: {
    readonly arenaLabel: string;
    readonly decorationsLabel: string;
    readonly reducedMotionLabel: string;
  };
  readonly arena: {
    readonly decorations: readonly SeasonalArenaDecoration[];
    readonly highContrastPalette: SeasonalArenaPalette;
    readonly palette: SeasonalArenaPalette;
  };
  readonly displayName: string;
  readonly enabled: boolean;
  readonly home: {
    readonly activeDescription: string;
    readonly icon: string;
    readonly upcomingDescription: string;
  };
  readonly mode: TimedModeId;
  readonly results: {
    readonly highlight: string;
    readonly modeLabel: string;
    readonly outcome: string;
  };
  readonly rule: {
    readonly description: string;
    readonly kind: "standard";
  } | null;
  readonly schedule: {
    readonly endUtc: string;
    readonly startUtc: string;
  };
  readonly seedNamespace: string;
  readonly share: {
    readonly callToAction: string;
    readonly displayName: string;
  };
  readonly slug: SeasonalChallengeSlug;
  readonly version: number;
}

export interface SeasonalChallengeDefinition {
  readonly descriptor: SeasonalChallengeDescriptor;
  readonly identity: SeasonalChallengeIdentity;
  readonly mode: TimedModeId;
  readonly seed: string;
}

export interface SeasonalChallengeResolution {
  readonly challenge: SeasonalChallengeDefinition | null;
  readonly descriptor: SeasonalChallengeDescriptor | null;
  readonly status: SeasonalChallengeStatus;
}

export interface SeasonalChallengeRegistry {
  readonly descriptors: readonly SeasonalChallengeDescriptor[];
  readonly find: (
    identity: SeasonalChallengeIdentity,
  ) => SeasonalChallengeDescriptor | null;
}

const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;
const utcTimestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u;
const seedNamespacePattern =
  /^snakely:seasonal:v[1-9][0-9]*:[a-z0-9]+(?:-[a-z0-9]+)*$/u;

const isRgb = (value: number): boolean =>
  Number.isSafeInteger(value) && value >= 0 && value <= 0xffffff;

const isNonEmptyText = (value: string): boolean => value.trim().length > 0;

const parseUtc = (value: string): number => {
  if (!utcTimestampPattern.test(value)) {
    return Number.NaN;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value
    ? parsed
    : Number.NaN;
};

const validateDecoration = (decoration: SeasonalArenaDecoration): void => {
  if (
    !Number.isFinite(decoration.alpha) ||
    decoration.alpha <= 0 ||
    decoration.alpha > 0.2
  ) {
    throw new RangeError("Season decoration alpha is invalid");
  }
  if (
    !Number.isFinite(decoration.xRatio) ||
    !Number.isFinite(decoration.yRatio) ||
    !Number.isFinite(decoration.widthRatio) ||
    !Number.isFinite(decoration.heightRatio) ||
    decoration.widthRatio <= 0 ||
    decoration.heightRatio <= 0 ||
    decoration.widthRatio > 0.24 ||
    decoration.heightRatio > 0.24 ||
    decoration.xRatio - decoration.widthRatio / 2 < 0.04 ||
    decoration.xRatio + decoration.widthRatio / 2 > 0.96 ||
    decoration.yRatio - decoration.heightRatio / 2 < 0.04 ||
    decoration.yRatio + decoration.heightRatio / 2 > 0.96
  ) {
    throw new RangeError("Season decoration is outside safe arena bounds");
  }
  if (decoration.color !== undefined && !isRgb(decoration.color)) {
    throw new RangeError("Season decoration color is invalid");
  }
};

export const seasonalChallengeIdentityFor = (
  descriptor: Pick<SeasonalChallengeDescriptor, "slug" | "version">,
): SeasonalChallengeIdentity =>
  `seasonal:v${String(descriptor.version)}:${descriptor.slug}` as SeasonalChallengeIdentity;

export const validateSeasonalChallengeDescriptor = (
  descriptor: SeasonalChallengeDescriptor,
): void => {
  if (!slugPattern.test(descriptor.slug)) {
    throw new RangeError("Season slug is invalid");
  }
  if (!Number.isSafeInteger(descriptor.version) || descriptor.version < 1) {
    throw new RangeError("Season version is invalid");
  }
  if (
    !isNonEmptyText(descriptor.displayName) ||
    !isNonEmptyText(descriptor.home.activeDescription) ||
    !isNonEmptyText(descriptor.home.icon) ||
    !isNonEmptyText(descriptor.home.upcomingDescription) ||
    !isNonEmptyText(descriptor.results.highlight) ||
    !isNonEmptyText(descriptor.results.modeLabel) ||
    !isNonEmptyText(descriptor.results.outcome) ||
    !isNonEmptyText(descriptor.share.callToAction) ||
    !isNonEmptyText(descriptor.share.displayName) ||
    !isNonEmptyText(descriptor.accessibility.arenaLabel) ||
    !isNonEmptyText(descriptor.accessibility.decorationsLabel) ||
    !isNonEmptyText(descriptor.accessibility.reducedMotionLabel)
  ) {
    throw new RangeError("Season copy is incomplete");
  }
  if (
    descriptor.rule !== null &&
    !isNonEmptyText(descriptor.rule.description)
  ) {
    throw new RangeError("Season rule copy is incomplete");
  }
  const startMs = parseUtc(descriptor.schedule.startUtc);
  const endMs = parseUtc(descriptor.schedule.endUtc);
  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    startMs >= endMs
  ) {
    throw new RangeError("Season schedule is invalid");
  }
  const expectedNamespace = `snakely:seasonal:v${String(descriptor.version)}:${descriptor.slug}`;
  if (
    !seedNamespacePattern.test(descriptor.seedNamespace) ||
    descriptor.seedNamespace !== expectedNamespace
  ) {
    throw new RangeError("Season seed namespace is invalid");
  }
  if (descriptor.mode !== "challenge-1") {
    throw new RangeError("Season duration must use the one-minute challenge");
  }
  const colors = [
    descriptor.arena.palette.backgroundColor,
    descriptor.arena.palette.boundaryColor,
    descriptor.arena.palette.boundaryOutlineColor,
    descriptor.arena.palette.decorationColor,
    descriptor.arena.palette.fieldColor,
    descriptor.arena.palette.foodHaloColor,
    descriptor.arena.palette.snakeOutlineColor,
    descriptor.arena.highContrastPalette.backgroundColor,
    descriptor.arena.highContrastPalette.boundaryColor,
    descriptor.arena.highContrastPalette.boundaryOutlineColor,
    descriptor.arena.highContrastPalette.decorationColor,
    descriptor.arena.highContrastPalette.fieldColor,
    descriptor.arena.highContrastPalette.foodHaloColor,
    descriptor.arena.highContrastPalette.snakeOutlineColor,
  ];
  if (!colors.every(isRgb)) {
    throw new RangeError("Season palette contains an invalid color");
  }
  if (descriptor.arena.decorations.length > 24) {
    throw new RangeError("Season has too many decorations");
  }
  for (const decoration of descriptor.arena.decorations) {
    validateDecoration(decoration);
  }
};

export const createSeasonalChallengeRegistry = (
  descriptors: readonly SeasonalChallengeDescriptor[],
): SeasonalChallengeRegistry => {
  const byIdentity = new Map<
    SeasonalChallengeIdentity,
    SeasonalChallengeDescriptor
  >();
  for (const descriptor of descriptors) {
    validateSeasonalChallengeDescriptor(descriptor);
    const identity = seasonalChallengeIdentityFor(descriptor);
    if (byIdentity.has(identity)) {
      throw new RangeError("Season registry contains a duplicate descriptor");
    }
    byIdentity.set(identity, descriptor);
  }
  const entries = [...descriptors];
  return {
    descriptors: entries,
    find: (identity) => byIdentity.get(identity) ?? null,
  };
};

const definitionFor = (
  descriptor: SeasonalChallengeDescriptor,
): SeasonalChallengeDefinition => ({
  descriptor,
  identity: seasonalChallengeIdentityFor(descriptor),
  mode: descriptor.mode,
  seed: `${descriptor.seedNamespace}:run`,
});

const statusAt = (
  descriptor: SeasonalChallengeDescriptor,
  nowMs: number,
): SeasonalChallengeStatus => {
  if (!descriptor.enabled) {
    return "disabled";
  }
  const startMs = parseUtc(descriptor.schedule.startUtc);
  const endMs = parseUtc(descriptor.schedule.endUtc);
  if (nowMs < startMs) {
    return "upcoming";
  }
  return nowMs >= endMs ? "expired" : "active";
};

export const resolveSeasonalChallenge = (
  registry: SeasonalChallengeRegistry,
  nowMs: number,
  identity?: SeasonalChallengeIdentity,
): SeasonalChallengeResolution => {
  if (!Number.isFinite(nowMs)) {
    return { challenge: null, descriptor: null, status: "unknown" };
  }
  const descriptor =
    identity === undefined
      ? (registry.descriptors.find(
          (candidate) => statusAt(candidate, nowMs) === "active",
        ) ??
        registry.descriptors.find(
          (candidate) => statusAt(candidate, nowMs) === "upcoming",
        ) ??
        registry.descriptors.find(
          (candidate) => statusAt(candidate, nowMs) === "expired",
        ) ??
        registry.descriptors[0] ??
        null)
      : registry.find(identity);
  if (descriptor === null) {
    return { challenge: null, descriptor: null, status: "unknown" };
  }
  const status = statusAt(descriptor, nowMs);
  return {
    challenge: status === "active" ? definitionFor(descriptor) : null,
    descriptor,
    status,
  };
};

const linearChannel = (channel: number): number => {
  const normalized = channel / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

const luminance = (color: number): number =>
  linearChannel((color >> 16) & 0xff) * 0.2126 +
  linearChannel((color >> 8) & 0xff) * 0.7152 +
  linearChannel(color & 0xff) * 0.0722;

export const contrastRatio = (first: number, second: number): number => {
  if (!isRgb(first) || !isRgb(second)) {
    throw new RangeError("Contrast color is invalid");
  }
  const brighter = Math.max(luminance(first), luminance(second));
  const darker = Math.min(luminance(first), luminance(second));
  return (brighter + 0.05) / (darker + 0.05);
};
